import React, { useCallback, useMemo } from "react";
import styles from "../AddEditAutoImportConfigurationsModal.module.css";
import { LRequiredInput } from "./CommonComponents/LRequiredInput";
import { FormGrid } from "./CommonComponents/FormGrid";
import { LFormInput } from "@loffa/gpp-modules";
import { useAutoImportFormContext } from "../../Context/AutoImportFormContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/sharp-solid-svg-icons";
import { CONNECTION_TYPES_DATA, CONNECTION_TYPES_ENUM } from "../../../constants/autoImportConnectionTypes";
import { KendoComboBoxEvent } from "@appTypes/kendoTypes";

export function SourceFileForm() {
  const { formData, updateField, updateFormData } = useAutoImportFormContext();
  const {
    fileHasHeaderRow,
    expectedFileName,
    archiveAfterProcessing,
    archivePath,
    ftpServer,
    sftpEnabled,
    ftpPort,
    ftpUsername,
    ftpPassword,
    sourceFileType,
    blobStorageFolder,
    blobStoragePassword,
    blobStorageUsername
  } = formData;

  // toggle SFTP + adjust port
  const handleSftpEnable = (e: any) => {
    const enabled = e.value as boolean;
    updateField("sftpEnabled", enabled);
    updateField("ftpPort", enabled ? 22 : 21);
  };
  
  const selectedConnectionType = useMemo(
    () => CONNECTION_TYPES_DATA.find(item => item?.value === sourceFileType),
    [sourceFileType]
  );

  const handleConnectionTypeChange = useCallback((e : KendoComboBoxEvent) => {
    const connectionType = e.target.value as {value:number, text:string};
    if(connectionType){
      //clear fields of alternate storage type option
      switch (connectionType.value){
        case CONNECTION_TYPES_ENUM.FTP: updateFormData({
            blobStorageUsername: "",
            blobStoragePassword:"",
            blobStorageFolder:"",
          });
          break
        case CONNECTION_TYPES_ENUM.BLOB: updateFormData({
            ftpServer: "",
            sftpEnabled: false,
            ftpPort: 0,
            ftpUsername: "",
            ftpPassword: "",
          });
          break
        default:
          break
      }
      return updateField("sourceFileType", connectionType.value)
    }
    updateField("sourceFileType", CONNECTION_TYPES_ENUM.SELECT)
  }, [sourceFileType, updateFormData]);

  const blobFields = [
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="Blob Storage Username"
          input={{
            name: "Blob Storage Username",
            placeholder: "Enter Blob Storage Username",
            value: blobStorageUsername || "",
            onChange: (e : React.ChangeEvent<HTMLInputElement>) => updateField("blobStorageUsername", e.target.value),
          }}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="Blob Storage Password"
          input={{
            name: "Blob Storage Password",
            placeholder: "Enter Blob Storage Password",
            value: blobStoragePassword || "",
            onChange: (e : React.ChangeEvent<HTMLInputElement>) => updateField("blobStoragePassword", e.target.value),
          }}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="Blob Storage Folder"
          input={{
            name: "Blob Storage Folder",
            placeholder: "Enter Blob Storage Folder",
            value: blobStorageFolder || "",
            onChange: (e : React.ChangeEvent<HTMLInputElement>) => updateField("blobStorageFolder", e.target.value),
          }}
        />
      ),
    },
  ];

  const ftpFields = [
    {
      colSpan: 12,
      render: () => (
        <div className={styles.checkboxInputAlign}>
          <LRequiredInput
            type="checkbox"
            required
            labelName="Enable SFTP"
            checkbox={{
              value: sftpEnabled,
              checked: sftpEnabled,
              onChange: handleSftpEnable,
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="FTP Server/Host"
          input={{
            name: "FTP Server/Host",
            placeholder: "Enter Server/Host",
            value: ftpServer || "",
            onChange: (e : React.ChangeEvent<HTMLInputElement>) =>
              updateField("ftpServer", e.target.value),
          }}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="FTP Port"
          input={{
            name: "FTP Port",
            placeholder: "Enter Port",
            type: "number",
            value: ftpPort,
            onChange: (e : React.ChangeEvent<HTMLInputElement>) =>
              updateField("ftpPort", Number(e.target.value)),
          }}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="FTP Username"
          input={{
            name: "FTP Username",
            placeholder: "Enter FTP Username",
            value: ftpUsername || "",
            onChange: (e : React.ChangeEvent<HTMLInputElement>) =>
              updateField("ftpUsername", e.target.value),
          }}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="FTP Password"
          input={{
            name: "FTP Password",
            placeholder: "Enter FTP Password",
            value: ftpPassword || "",
            onChange: (e : React.ChangeEvent<HTMLInputElement>) =>
              updateField("ftpPassword", e.target.value),
          }}
        />
      ),
    }
  ];


  const inputs = [
    {
      colSpan: 12,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelAlign="top"
            required
            labelName="Connection Type"
            comboBox= {{
              data: CONNECTION_TYPES_DATA,
              textField: "text",
              dataItemKey: "value",
              placeholder: "Select a Connection Type",
              value: selectedConnectionType,
              onChange: handleConnectionTypeChange
            }}
          />
        </div>
      ),
    },
    ...(selectedConnectionType?.value === CONNECTION_TYPES_ENUM.FTP
      ? ftpFields
      : selectedConnectionType?.value === CONNECTION_TYPES_ENUM.BLOB
      ? blobFields
      : [])
      ,
    {
      colSpan: 12,
      render: () => (
        <div className={styles.checkboxInputAlign}>
          <LFormInput
            type="checkbox"
            labelName="File Has Header Row"
            checkbox={{
              value: fileHasHeaderRow,
              checked: fileHasHeaderRow,
              onChange: (e: any) =>
                updateField("fileHasHeaderRow", e.value),
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <LRequiredInput
          type="input"
          required
          labelAlign="top"
          labelName="File Name Matcher"
          input={{
            name: "Expected File Name",
            placeholder: "Enter File Name Matcher",
            value: expectedFileName,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              updateField("expectedFileName", e.target.value),
          }}
        />
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <div className={styles.infoNote}>
          <FontAwesomeIcon
            icon={faQuestionCircle}
            className={styles.warningIcon}
          />
          <div className={styles.noteContent}>
            <strong>Note:</strong> Enter a regular expression to match
            the filename.
            <ul>
              <li>
                <code>^PBINS1234.*IDCONF.*$</code> - filenames{" "}
                <strong>starting</strong> with "PBINS1234" and containing
                "IDCONF"
                <br />
                (e.g.
                <code>
                  PBINS1234_05152025_IDCONF_J001505.csv
                </code>
                )
              </li>
              <li>
                <code>PBINS1234.*IDCONF</code> - filenames{" "}
                <strong>containing</strong> "PBINS1234" and "IDCONF" anywhere
                <br />
                (e.g.
                <code>
                  archive_PBINS1234_20250915_IDCONF_report.csv
                </code>
                )
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <div className={styles.checkboxInputAlign}>
          <LFormInput
            type="checkbox"
            required
            labelName="Archive After Processing"
            checkbox={{
              value: archiveAfterProcessing,
              checked: archiveAfterProcessing,
              onChange: (e: any) =>
                updateField("archiveAfterProcessing", e.value),
            }}
          />
        </div>
      ),
    },
    // only show the path input if archiving is enabled
    ...(archiveAfterProcessing
      ? [
          {
            colSpan: 12,
            render: () => (
              <LFormInput
                type="input"
                labelAlign="top"
                labelName="Archive Path"
                input={{
                  disabled: !archiveAfterProcessing,
                  name: "Archive Path",
                  placeholder: "Enter Archive Path",
                  value: archivePath,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    updateField("archivePath", e.target.value),
                }}
              />
            ),
          },
        ]
      : []),
  ];
  return <FormGrid items={inputs} />;
}

function updateFormData(arg0: { client: any; productType: undefined; clientProductGuid: string; forDtc: any; }) {
  throw new Error("Function not implemented.");
}
